import 'react-datepicker/dist/react-datepicker.css';

import { forwardRef, useRef } from 'react';
import DatePicker from 'react-datepicker';

import TextPrompt from '../text-prompt';

interface IDateTimeInput extends Omit<React.ComponentPropsWithoutRef<'input'>, 'value' | 'onChange'> {
	label?: string;
	error?: string;
	isonlyview?: boolean;
	minComponent?: boolean;
	helperText?: string;
	isRequired?: boolean;
	dateValue?: Date | null;
	dateOnChange?: (date: Date | null) => void;
	minDate?: Date | null;
	maxDate?: Date | null;
	/** Minutes between selectable times. 15 keeps the dropdown scannable; 1 makes it a list of 1,440. */
	timeIntervals?: number;
	/** Extra classes for the text input itself — e.g. `form-control form-control-sm` to match sibling fields. */
	inputClassName?: string;
}

/**
 * A date AND time picker, for fields where the hour is part of the answer.
 *
 * <p>Distinct from {@link TypeDateInput}, which is date-only and formats `dd/MM/yyyy`. A
 * scheduled broadcast needs "every week at 09:00", and a date-only control cannot say that —
 * it silently commits whatever midnight the parser chose.</p>
 *
 * <p><b>It hands back a `Date`, never a string, and that is the point.</b> The obvious
 * alternative — a native `<input type="datetime-local">` — yields `"2026-09-05T09:00"` with no
 * zone, and the two halves of the round trip then disagree in a way that is almost impossible
 * to spot: `new Date(local).toISOString()` correctly converts to UTC on the way out, but reading
 * the stored ISO string back by slicing it (`value.split('T')[1]`, which is what the coupon form
 * does for dates today) prints the UTC hour. In WAT that is a constant one-hour error with no
 * DST to make it flicker, so it reads as "the server runs an hour behind" rather than as a
 * display bug, and it survives.</p>
 *
 * <p>A `Date` has no such ambiguity: the picker owns local wall time, `toISOString()` owns the
 * wire, and `toLocaleString()` owns the display. Convert at those two edges only.</p>
 */
export const TypeDateTimeInput = forwardRef<HTMLInputElement, IDateTimeInput>(
	(
		{
			label,
			error,
			isonlyview,
			minComponent,
			helperText,
			isRequired,
			dateValue,
			dateOnChange,
			minDate,
			maxDate,
			timeIntervals = 15,
			inputClassName,
			...props
		},
		_ref
	) => {
		// The right panel closes on ANY Escape that reaches `document`, so an Escape meant for the open
		// calendar used to close the whole panel and discard the form. The picker handles the key first
		// (closing itself); this stops it there. Bubble phase, not capture, so the picker still sees it.
		const calendarOpenRef = useRef(false);
		const keepEscapeInPicker = (event: React.KeyboardEvent) => {
			if (event.key === 'Escape' && calendarOpenRef.current) event.stopPropagation();
		};

		return (
			<div
				className={`form-container position-relative ${props.className || ''}`}
				onKeyDown={keepEscapeInPicker}
			>
				{label && (
					<div className='f-row-17 align-items-center pb-2'>
						<label
							htmlFor={props.id || props.name}
							className='m-0'
						>
							{label}
							{isRequired && (
								<span
									aria-hidden='true'
									style={{ color: 'var(--color-danger)' }}
								>
									{' '}
									*
								</span>
							)}
						</label>
					</div>
				)}
				<DatePicker
					id={props.id || props.name}
					selected={dateValue ?? null}
					// Annotated `: void` deliberately. `DatePickerProps` is a discriminated union over
					// `selectsRange` / `selectsMultiple`, and an `onChange` whose type is optional or
					// returns `void | undefined` matches none of the three branches cleanly — TypeScript
					// then reports the LAST branch's discriminant (`selectsMultiple`) as missing, which
					// reads as a completely unrelated error about multi-select.
					onChange={(date: Date | null): void => {
						dateOnChange?.(date);
					}}
					minDate={minDate ?? undefined}
					maxDate={maxDate ?? undefined}
					showTimeSelect
					// Left-aligned to the field. The default centres the calendar under the input, so a narrow
					// field near a panel's left edge pushed the calendar's first column out of view.
					popperPlacement='bottom-start'
					timeIntervals={timeIntervals}
					onCalendarOpen={() => {
						calendarOpenRef.current = true;
					}}
					onCalendarClose={() => {
						calendarOpenRef.current = false;
					}}
					timeCaption='Time'
					dateFormat='dd/MM/yyyy HH:mm'
					timeFormat='HH:mm'
					placeholderText='DD/MM/YYYY HH:mm'
					disabled={props.disabled || isonlyview}
					aria-required={isRequired ? 'true' : undefined}
					aria-invalid={error ? 'true' : undefined}
					className={`${error ? 'is-error' : ''} ${isonlyview ? 'isonlyview' : ''} ${minComponent ? 'min-size' : ''} ${inputClassName ?? ''}`}
				/>
				{!!helperText && !error && (
					<p
						className='m-0 pt-1'
						style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-secondary)' }}
					>
						{helperText}
					</p>
				)}
				{!!error && (
					<TextPrompt
						prompt={error}
						status={false}
					/>
				)}
			</div>
		);
	}
);

TypeDateTimeInput.displayName = 'TypeDateTimeInput';
